import { useMutation, useQuery } from "react-query";
import { deleteUser, getActionPinValidity, getUser, getUsers, patchUser, postClearUserTokens, postGenerateUserPassword, postUser } from "./requests";
import { notifyError, notifySuccess } from "@/toast-notifications";
import queryClient from "../queryClient";
import useReduxStore from "@/hooks/useReduxStore";
import { updateUserSettingsSuccess } from "@/store/actions";

export const GET_ACTION_PIN_VALIDITY_KEY = "get-action-pin-validity";
export const useGetActionPinValidity = (actionPin, options = {}) =>
    useQuery(
        [GET_ACTION_PIN_VALIDITY_KEY, actionPin],
        () => getActionPinValidity(actionPin),
        {
            ...options,
            staleTime: 0, 
            cacheTime: 0,
        },
    );

export const USERS_KEY = "users";

export const useGetUsers = (options = {}) =>
    useQuery({
        ...options,
        queryKey: [USERS_KEY],
        queryFn: getUsers,
        onError: () => {
            notifyError("Une erreur est survenue lors de la récupération des utilisateurs.");
        },
    });

export const useGetUser = (id, options = {}) =>
    useQuery({
        ...options,
        queryKey: [USERS_KEY, id],
        queryFn: () => getUser(id),
        onError: () => {
            notifyError("Une erreur est survenue lors de la récupération de l'utilisateur.");
        },
    });

export const usePostUser = (options = {}) =>
    useMutation({
        ...options,
        mutationFn: postUser,
        onSuccess: user => {
            queryClient.setQueryData(
                [USERS_KEY],
                list => [...list, user],
            );

            queryClient.setQueryData(
                [USERS_KEY, user?.id],
                user,
            );

            notifySuccess("L'utilisateur a été ajouté avec succès.");
        },
        onError: () => {
            notifyError("Une erreur est survenue lors de l'ajout de l'utilisateur.");
        },
    });

export const usePatchUser = (options = {}) => {

    const { slice: currentUser, dispatch } = useReduxStore(state => state.authenticationReducer.userSettings);

    return useMutation({
        ...options,
        mutationFn: patchUser,
        onSuccess: user => {
            queryClient.setQueryData(
                [USERS_KEY],
                list => list.map(u => u.id === user.id ? user: u),
            );

            queryClient.setQueryData(
                [USERS_KEY, user?.id],
                user,
            );

            // Legacy adaptation            
            if(currentUser?.id === user.id)
                dispatch(updateUserSettingsSuccess(user));

            notifySuccess("L'utilisateur a été modifié avec succès.");
        },
        onError: () => {
            notifyError("Une erreur est survenue lors de la modification de l'utilisateur.");
        },
    });
}

export const useDeleteUser = (options = {}) =>
    useMutation({
        ...options,
        mutationFn: deleteUser,
        onSuccess: user => {
            queryClient.setQueryData(
                [USERS_KEY],
                list => list.filter(u => u.id !== user.id),
            );

            queryClient.removeQueries({
                queryKey: [USERS_KEY, user?.id],
            });

            notifySuccess("L'utilisateur a été supprimé avec succès.");
        },
        onError: () => {
            notifyError("Une erreur est survenue lors de la suppression de l'utilisateur.");
        },
    });

export const usePostClearUserTokens = (options = {}) =>
    useMutation({
        ...options,
        mutationFn: postClearUserTokens,
        onSuccess: () => {
            notifySuccess("Les connexions de l'utilisateur ont été révoquées avec succès.");
        },
        onError: () => {
            notifyError("Une erreur est survenue lors de la révocation des connexions de l'utilisateur.");
        },
    });

export const usePostGenerateUserPassword = (options = {}) =>
    useMutation({
        ...options,
        mutationFn: postGenerateUserPassword,
        onSuccess: () => {
            notifySuccess("Le mot de passe de l'utilisateur a été généré avec succès.");
        },
        onError: () => {
            notifyError("Une erreur est survenue lors de la génération du mot de passe de l'utilisateur.");
        },
    });


// WEBPACK FOOTER //
// ./src/queries/users/hooks.js